'use client';

import { forwardRef, useState, ReactNode, Ref } from 'react';
import { type Address, type AddressCoords } from '@/lib/types';
import {
  BaseSearchField,
  type BaseSearchFieldApi,
  type TextFieldProps,
} from '@/primitives';
import {
  geocodeByAddress,
  geocoderResultToAddress,
  geocoderResultToAddressCoords,
  getAutocompleteResultsForAddress,
} from '@/lib/googleMaps';
import { GooglePlacesScript } from '@/components/GooglePlacesScript';

export interface GoogleAddressSearchFieldProps {
  label: ReactNode;
  placeholder: string;
  InputLabelProps?: TextFieldProps['InputLabelProps'];
  InputProps?: TextFieldProps['InputProps'];
  fullWidth?: boolean;
  disabled?: boolean;
  onChange: (
    address: Address | null,
    addressCoords: AddressCoords | null,
  ) => void;
}

export const GoogleAddressSearchField = forwardRef(
  function GoogleAddressSearchFieldInner(
    {
      label,
      placeholder,
      InputLabelProps,
      InputProps,
      fullWidth,
      disabled,
      onChange,
    }: GoogleAddressSearchFieldProps,
    ref: Ref<BaseSearchFieldApi>,
  ) {
    const [scriptReady, setScriptReady] = useState(
      typeof window !== 'undefined' && !!window.google?.maps?.places,
    );

    const handleScriptLoad = () => {
      setScriptReady(true);
    };

    const [value, setValue] =
      useState<google.maps.places.AutocompletePrediction | null>(null);

    const fetchOptions = async (inputValue: string) => {
      if (
        !scriptReady ||
        typeof window === 'undefined' ||
        !window.google?.maps?.places
      ) {
        return [];
      }

      const result = await getAutocompleteResultsForAddress(inputValue);
      if (Array.isArray(result)) {
        return result;
      }
      return [];
    };

    const handleChange = async (
      value: google.maps.places.AutocompletePrediction | null,
    ) => {
      if (
        !scriptReady ||
        typeof window === 'undefined' ||
        !window.google?.maps?.places
      ) {
        return;
      }

      const results = await geocodeByAddress(value?.description || '');

      if (Array.isArray(results) && results.length > 0) {
        const address = geocoderResultToAddress(results[0]);
        const addressCoords = await geocoderResultToAddressCoords(results[0]);
        onChange(address, addressCoords);
      }

      setValue(value);
    };

    return (
      <>
        <GooglePlacesScript onLoad={handleScriptLoad} />
        <BaseSearchField<google.maps.places.AutocompletePrediction>
          autoCompleteId="search-google-address"
          disabled={disabled}
          fetchOptions={fetchOptions}
          freeSolo
          fullWidth={fullWidth}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.description || ''
          }
          InputLabelProps={InputLabelProps}
          InputProps={InputProps}
          isOptionEqualToValue={(option, value) =>
            option.description === value.description
          }
          label={label}
          placeholder={placeholder}
          ref={ref}
          renderOption={(props, option) => {
            let key = '';
            if ('key' in props) {
              key = props.key as string;
            }
            return (
              <li {...props} key={key}>
                {option.description}
              </li>
            );
          }}
          value={value}
          onChange={handleChange}
        />
      </>
    );
  },
);
